import React, { useEffect } from 'react'
import { connect } from 'dva'
import * as webMercatorUtils from '@arcgis/core/geometry/support/webMercatorUtils'
import dayjs from 'dayjs'
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer'
import PropTypes from 'prop-types'
import SketchWidget from '@arcgis/core/widgets/Sketch'

import { FEATURE_COLOR, MAP_LAYER_IDS, FEATURE_TYPES, MAP_MODES, MAP_TOOLS } from '../../../lib/constants'
import { getItemIndex } from '../../../lib/utils'
import actions from './actions'
import styles from './SketchWidget.module.scss'

function GHSketchWidget(props) {
  useEffect(() => {
    const params = {
      items: props.map.layers.items,
      key: 'id',
      reverse: true,
      value: MAP_LAYER_IDS.sketch
    }
    let sketchLayerIndex = getItemIndex(params)
    if (sketchLayerIndex > -1) {
      props.map.layers.remove(params.items[sketchLayerIndex])
    } else {
      params.value = MAP_LAYER_IDS.gohunt
      sketchLayerIndex = getItemIndex(params)  
    }

    const newSketchLayer = new GraphicsLayer({
      id: MAP_LAYER_IDS.sketch,
      mode: 'on-the-ground'
    })

    const sketch = new SketchWidget({
      id: 'sketch',
      layer: newSketchLayer,
      view: props.view,
      creationMode: 'single',
      availableCreateTools: ['point', 'polyline', 'polygon'],
      defaultCreateOptions: {
        hasZ: props.mapMode === MAP_MODES.ThreeD,
        mode: 'hybrid'
      }
    })
    sketch.viewModel.updateOnGraphicClick = false

    props.setSketchWidgetRef(sketch)

    const sketchEventHandler = sketch.on('create', async e => {
      if (e.state === 'complete') {
        newSketchLayer.removeAll()
        const curTimestamp = dayjs().valueOf()

        const geometry = webMercatorUtils.webMercatorToGeographic(e.graphic.geometry)
        let feature = null

        props.showCalculatingToast()

        switch (e.graphic.geometry.type) {
          case 'point': {
            let elevation = 0.0
            if (props.mapMode == MAP_MODES.ThreeD) {
              elevation = props.view.groundView.elevationSampler.queryElevation(geometry).z
            } else {
              elevation = 0.0
              // elevation = await get2DElevation(e.graphic.geometry);
            }

            feature = {
              uuid: `temp-point-${curTimestamp}`,
              title: `Waypoint ${dayjs().format('MM/DD/YYYY hh:mm')}`,
              ghType: FEATURE_TYPES.Point,
              geometry: {
                coordinates: [geometry.x, geometry.y, elevation],
                type: 'Point'
              },
              properties: {
                color: FEATURE_COLOR.Orange.rgba,
                icon: 'gohunt-logo',
                ghType: FEATURE_TYPES.Point
              }
            }

            break
          }
          case 'polyline': {
            /*
              #TODO: newGeometry is used as a fix due to the PostGIS migration which
              now includes a z column. This can potentially be revisited if the codebase
              moves towards utilizing full 3D, as we will always have a z-value.
              -- WD - July 21, 2021
            */
            let newGeometry = []
            if (props.mapMode == MAP_MODES.ThreeD) {
              newGeometry = geometry.paths[0]
            } else {
              //   // newGeometry = await getMultipointElevations(geometry.paths[0]);
              newGeometry = geometry.paths[0].map(point => [point[0], point[1], 0.0])
            }

            feature = {
              uuid: `temp-line-${curTimestamp}`,
              title: `Line ${dayjs().format('MM/DD/YYYY hh:mm')}`,
              ghType: FEATURE_TYPES.Polyline,
              geometry: {
                coordinates: newGeometry,
                type: 'LineString'
              },
              properties: {
                color: FEATURE_COLOR.Orange.rgba,
                ghType: FEATURE_TYPES.Polyline,
                strokeWidth: 4,
                strokeType: 'solid'
              }
            }
            break
          }
          case 'polygon': {
            let newGeometry = []
            if (props.mapMode == MAP_MODES.ThreeD) {
              newGeometry = geometry.rings[0]
            } else {
              // newGeometry = await getMultipointElevations(geometry.rings[0]);
              newGeometry = geometry.rings[0].map(point => [point[0], point[1], 0.0])
            }

            feature = {
              uuid: `temp-polygon-${curTimestamp}`,
              title: `Area ${dayjs().format('MM/DD/YYYY hh:mm')}`,
              ghType: FEATURE_TYPES.Polygon,
              geometry: {
                coordinates: [newGeometry],
                type: 'Polygon'
              },
              properties: {
                color: FEATURE_COLOR.Orange.rgba,
                ghType: FEATURE_TYPES.Polygon,
                strokeWidth: 4,
                strokeType: 'solid'
              }
            }
            break
          }
          default:
            break
        }

        props.createFeature(feature)
      }
    })
    props.map.add(newSketchLayer, sketchLayerIndex)
    return () => {
      if (sketchEventHandler) {
        sketchEventHandler.remove()
      }
      // Destroy the Sketch widget so it tears down its own internal graphics layer
      // (auto-id'd, otherwise orphaned on the map on every view recreation).
      if (sketch) {
        sketch.destroy()
      }
      if (newSketchLayer && props.map) {
        props.map.remove(newSketchLayer)
      }
    }
  }, [props.view])

  if (!props.isMapReady) return null

  return (
    <div
      className={[styles.container, 'esri-component esri-sketch esri-widget'].join(' ')}
      style={{ visibility: props.isUpdatingLocation ? 'hidden' : 'visible' }}
    >
      <div className='esri-sketch__panel'>
        <div className={styles.toolSection}>
          <button
            aria-label='Drop a waypoint'
            className={[`esri-sketch__button esri-icon-map-pin ${styles.pinButton}`, props.activeTool === MAP_TOOLS.Sketch_Point ? styles.selected : ''].join(
              ' '
            )}
            title='Drop a waypoint'
            onClick={props.activeTool === MAP_TOOLS.Sketch_Point ? props.cancelWidget : props.beginCreatingPoint}
          ></button>
          <button
            aria-label='Draw a line'
            className={[
              `esri-sketch__button esri-icon-map-polyline ${styles.polylineButton}`,
              props.activeTool === MAP_TOOLS.Sketch_Polyline ? styles.selected : ''
            ].join(' ')}
            title='Draw a line'
            onClick={props.activeTool === MAP_TOOLS.Sketch_Polyline ? props.cancelWidget : props.beginCreatingPolyline}
          ></button>
          <button
            aria-label='Draw an area'
            className={[
              `esri-sketch__button esri-icon-map-polygon ${styles.polygonButton}`,
              props.activeTool === MAP_TOOLS.Sketch_Polygon ? styles.selected : ''
            ].join(' ')}
            title='Draw an area'
            onClick={props.activeTool === MAP_TOOLS.Sketch_Polygon ? props.cancelWidget : props.beginCreatingPolygon}
          ></button>
        </div>
      </div>
    </div>
  )
}

GHSketchWidget.propTypes = {
  activeTool: PropTypes.string,
  isMapReady: PropTypes.bool,
  isUpdatingLocation: PropTypes.bool,
  map: PropTypes.any,
  mapMode: PropTypes.string,
  view: PropTypes.any,
  // actions
  beginCreatingPoint: PropTypes.func,
  beginCreatingPolygon: PropTypes.func,
  beginCreatingPolyline: PropTypes.func,
  cancelWidget: PropTypes.func,
  createFeature: PropTypes.func,
  setSketchWidgetRef: PropTypes.func,
  showCalculatingToast: PropTypes.func,
}

export default connect(model => {
  const { activeTool, isMapReady, isUpdatingLocation, mapMode } = model.map
  return {
    activeTool,
    isMapReady,
    isUpdatingLocation,
    mapMode
  }
}, actions)(GHSketchWidget)

// const addZeroZ = (geometry) => {
//   return geometry.map((point) => [...point, 0.0]);
// };

// const get2DElevation = (geometry) => {
//   return new Promise((resolve, reject) => {
//     const elevation = new ElevationLayer({
//       url: 'http://elevation3d.arcgis.com/arcgis/rest/services/WorldElevation3D/Terrain3D/ImageServer',
//     });

//     return elevation
//       .load()
//       .then((elevationSampler) => {
//         elevationSampler
//           .queryElevation(geometry, {
//             demResolution: 5, // Less than 5 seems to cause problems, so use 5, which is still very accurate.
//           })
//           .then((result) => {
//             return resolve(result.geometry.z);
//           })
//           .catch((e) => {
//             console.log('Cannot find elevation.');
//             return resolve(0); // Create waypoint with 0 elevation.
//           });
//       })
//       .catch((e) => {
//         console.log('Query elevation error', e);
//         return resolve(0); // Create waypoint with 0 elevation.
//       });
//   });
// };

// const getMultipointElevations = (geometry) => {
//   return new Promise((resolve, reject) => {
//     const elevation = new ElevationLayer({
//       url: 'http://elevation3d.arcgis.com/arcgis/rest/services/WorldElevation3D/Terrain3D/ImageServer',
//     });

//     return elevation
//       .load()
//       .then((elevationSampler) => {
//         elevationSampler
//           .queryElevation(new Multipoint({ points: geometry }), {
//             demResolution: 5,
//           })
//           .then((result) => {
//             return resolve(result.geometry.points);
//           })
//           .catch((e) => {
//             console.error('Cannot find elevation.');
//             return resolve(addZeroZ(geometry)); // Create waypoint with 0 elevation.
//           });
//       })
//       .catch((e) => {
//         console.error('Query elevation error', e);
//         return resolve(addZeroZ(geometry)); // Create waypoint with 0 elevation.
//       });
//   });
// };
